import React, { ReactNode } from 'react';
import { Button, DialogTitle, DialogContent, DialogActions,
  Dialog, DialogProps, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SimpleFormTextField from './SimpleFormDialogParts/SimpleFormTextField';
import SimpleFormLabel from './SimpleFormDialogParts/SimpleFormLabel';
import SimpleFormToggleField from './SimpleFormDialogParts/SimpleFormToggleField';
import ConfirmationDialog from './ConfirmationDialog';

// Export the other parts to simplify imports
export { SimpleFormTextField, SimpleFormLabel, SimpleFormToggleField };

/*
  Simple form dialog that can be used to edit some form data.

  Usage Example:
    <SimpleFormDialog
      isOpen={clearDialogOpen}
      title={i18n.t("dashboard.clear-widgets")}
      description={i18n.t("dashboard.clear-widgets-description")}
      onClose={handleClearWidgets}
    >
      { /* Form Content Here * / }
      <SimpleFormTextField>Field</SimpleFormTextField>
    </SimpleFormDialog>

  See SimpleFormDialogRawProps below for additional options.
  Add NEW generic form fields to the bottom of this file.
*/

export interface SimpleFormDialogRawProps extends Omit<DialogProps, ('onClose' | 'open' | 'title')> {
  // Is the dialog open?
  isOpen: boolean;

  // Callbacks to handle the user's action.
  // onCancel should close the dialog with no changes,
  // onConfirm should close the dialog and apply the user's changes.
  onCancel: () => void;
  onConfirm: () => void;

  // Callback for deletion of records - If this is provided, a delete button will be shown
  // that uses a confirmation dialog before calling onDelete.
  onDelete?: () => void;

  // Callback for handling selecting/deselecting all items
  onSelectAll?: () => void;
  onDeselectAll?: () => void;

  hideConfirmButton?: boolean;

  // The title of the dialog
  title: string | ReactNode;

  // Optional descriptive text to display below the title.
  // Alternately, you can pass children to display custom JSX
  description?: string | ReactNode;

  // Optional color for the confirm button, defaults to "success"
  confirmColor?: "success" | "error" | "warning" | "info";

  // Optional custom confirmation button text
  confirmText?: string | ReactNode;

  // Optional custom cancellation button text
  cancelText?: string | ReactNode;
}

export default function SimpleFormDialog(props: SimpleFormDialogRawProps) {
  const { confirmColor, confirmText, cancelText, title, onConfirm, isOpen, hideConfirmButton, onDelete, ...otherProps } = props;
  const theme = useTheme();
  const { i18n } = useTranslation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [ deleteConfirmationOpen, setDeleteConfirmationOpen ] = React.useState(false);

  const handleCancel = (event:any) => {
    props.onCancel(event);
  };

  const handleSubmit = (event:any) => {
    onConfirm(event);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      className='drag-cancel'
      fullScreen={isSmallScreen}
      {...otherProps}
    >
      <DialogTitle sx={{ minWidth: "400px" }}>
        <Typography variant="h4">
          {title}
        </Typography>
        { props.description &&
          <Typography variant="body1">
            {props.description}
          </Typography>
        }
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: theme.palette.background.default, p: 1 }} >
        {props.children}
      </DialogContent>
      <DialogActions sx={{ padding: "10px 10px 10px 0" }} >
        {props.onSelectAll && props.onDeselectAll &&
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={props.onSelectAll}
            >
              {i18n.t("select")} {i18n.t("all")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={props.onDeselectAll}
            >
              {i18n.t("deselect")} {i18n.t("all")}
            </Button>
          </>
        }
        {onDelete &&
          <Button
            sx={{ mr: 2, color: theme.palette.error.main }}
            type="button"
            startIcon={ null }
            onClick={() => { setDeleteConfirmationOpen(true); }}
            variant="text"
            size="large"
          >
            {i18n.t('delete')}
          </Button>
        }
        <Button
          size="small"
          variant="contained"
          color="secondary"
          onClick={(event) => handleCancel(event)} >
          { cancelText || i18n.t("cancel") }
        </Button>
        {!hideConfirmButton &&
          <Button
            variant="contained"
            color={confirmColor || "success"}
            size="small"
            onClick={handleSubmit} >
            { confirmText || i18n.t("save") }
          </Button>
        }
        <ConfirmationDialog
          isOpen={deleteConfirmationOpen}
          title={i18n.t("delete")}
          description={i18n.t("delete-confirmation-description")}
          onClose={(isConfirmed:boolean) => {
            if (isConfirmed && onDelete) {
              onDelete();
            } else {
              setDeleteConfirmationOpen(false);
            }
          }}
        ></ConfirmationDialog>
      </DialogActions>
    </Dialog>
  );
}
